import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useT } from '../../i18n';

const T = {
  es: {
    titles: {
      visibility: 'Vea Cadenia Visibility en Acción',
      intelligence: 'Experimente Cadenia Intelligence',
      documents: 'Descubra Cadenia Documents',
      default: 'Vea Cadenia en Acción',
    },
    descriptions: {
      visibility: 'Vea cómo centralizar la información de todos sus transportistas y gestionar envíos con trazabilidad completa.',
      intelligence: 'Descubra cómo analizar el rendimiento de su red logística con dashboards, agentes de IA e informes automáticos.',
      documents: 'Conozca la generación de documentos de transporte digitales con firma electrónica y cumplimiento normativo.',
      default: 'Demo gratuita con casos reales de su industria.',
    },
    noCommitment: 'Sin compromiso, con resultados inmediatos.',
    customizeDemo: 'Personalice su demo - Seleccione sus áreas de interés:',
    selectedSingular: 'área seleccionada para personalizar su demo',
    selectedPlural: 'áreas seleccionadas para personalizar su demo',
    processTitle: 'Proceso Simple y Rápido',
    processSubtitle: 'En aproximadamente dos semanas, de la consulta inicial a la propuesta final',
    steps: [
      {
        number: '1',
        title: 'Análisis de Necesidades',
        description: 'Evaluamos sus desafíos logísticos actuales y objetivos de negocio'
      },
      {
        number: '2',
        title: 'Demo Personalizada',
        description: 'Presentación en vivo adaptada a su industria y casos de uso'
      },
      {
        number: '3',
        title: 'Propuesta Comercial',
        description: 'Solución propuesta, plan de implementación y pricing transparente'
      }
    ],
    formTitle: '¿Listo para Optimizar su Logística?',
    formSubtitle: 'Únase a las empresas que ya confían en Cadenia para gestionar sus operaciones.',
    formTagline: 'Demo gratuita • Sin compromiso • Implementación ágil',
    placeholders: {
      name: 'Nombre Completo',
      email: 'Email de trabajo',
      company: 'Empresa',
      phone: 'Teléfono',
      message: 'Mensaje (opcional)',
    },
    selectedLabel: 'Áreas de interés seleccionadas:',
    consent: 'Doy el consentimiento para tratar mis datos con el fin de responder a mi solicitud',
    notifications: {
      rateLimit: 'Por favor, espere unos segundos antes de enviar otro mensaje.',
      tooFast: 'Por favor, tómese un momento para revisar el formulario.',
      tooMany: 'Demasiados intentos. Por favor, recargue la página.',
      success: '¡Gracias! Nos pondremos en contacto con usted pronto.',
      error: 'Error al enviar el formulario. Por favor, inténtelo de nuevo.',
    },
    submit: {
      sending: 'Enviando...',
      personalized: 'Solicitar Demo Personalizada',
      now: 'Solicitar Demo Ahora',
    },
  },
  en: {
    titles: {
      visibility: 'See Cadenia Visibility in Action',
      intelligence: 'Experience Cadenia Intelligence',
      documents: 'Discover Cadenia Documents',
      default: 'See Cadenia in Action',
    },
    descriptions: {
      visibility: 'See how to centralize information from all your carriers and manage shipments with full traceability.',
      intelligence: 'Discover how to analyze the performance of your logistics network with dashboards, AI agents and automated reports.',
      documents: 'Learn how digital transport documents are generated with electronic signature and regulatory compliance.',
      default: 'Free demo with real cases from your industry.',
    },
    noCommitment: 'No commitment, with immediate results.',
    customizeDemo: 'Customize your demo - Select your areas of interest:',
    selectedSingular: 'area selected to customize your demo',
    selectedPlural: 'areas selected to customize your demo',
    processTitle: 'A Simple, Fast Process',
    processSubtitle: 'In roughly two weeks, from initial consultation to final proposal',
    steps: [
      {
        number: '1',
        title: 'Needs Analysis',
        description: 'We assess your current logistics challenges and business goals'
      },
      {
        number: '2',
        title: 'Personalized Demo',
        description: 'A live presentation tailored to your industry and use cases'
      },
      {
        number: '3',
        title: 'Commercial Proposal',
        description: 'Proposed solution, implementation plan and transparent pricing'
      }
    ],
    formTitle: 'Ready to Optimize Your Logistics?',
    formSubtitle: 'Join the companies that already trust Cadenia to manage their operations.',
    formTagline: 'Free demo • No commitment • Fast implementation',
    placeholders: {
      name: 'Full Name',
      email: 'Work Email',
      company: 'Company',
      phone: 'Phone',
      message: 'Message (optional)',
    },
    selectedLabel: 'Selected areas of interest:',
    consent: 'I consent to the processing of my data in order to respond to my request',
    notifications: {
      rateLimit: 'Please wait a few seconds before sending another message.',
      tooFast: 'Please take a moment to review the form.',
      tooMany: 'Too many attempts. Please reload the page.',
      success: 'Thank you! We will get back to you shortly.',
      error: 'There was an error sending the form. Please try again.',
    },
    submit: {
      sending: 'Sending...',
      personalized: 'Request a Personalized Demo',
      now: 'Request a Demo Now',
    },
  },
};

const ProductCTA = ({ title, features, productName }) => {
  const t = useT(T);
  const [selectedFeatures, setSelectedFeatures] = useState([]);
  const [formData, setFormData] = useState({
    name: '', email: '', company: '', phone: '', message: '', honeypot: ''
  });
  const [timestamp] = useState(Date.now());
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [notification, setNotification] = useState(null);
  const [submitAttempts, setSubmitAttempts] = useState(0);
  const [lastSubmit, setLastSubmit] = useState(0);

  const getProductTitle = () => {
    return t.titles[productName] || t.titles.default;
  };

  const getProductDescription = () => {
    return t.descriptions[productName] || t.descriptions.default;
  };

  const toggleFeature = (feature) => {
    setSelectedFeatures(prev =>
      prev.includes(feature)
        ? prev.filter(f => f !== feature)
        : [...prev, feature]
    );
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    // Protecciones básicas
    const now = Date.now();
    const timeSinceLoad = now - timestamp;
    const timeSinceLastSubmit = now - lastSubmit;

    // Honeypot check
    if (formData.honeypot) {
      return; // Bot detectado
    }

    // Rate limiting
    if (timeSinceLastSubmit < 5000) {
      setNotification({ type: 'warning', message: t.notifications.rateLimit });
      return;
    }

    // Tiempo mínimo en página
    if (timeSinceLoad < 3000) {
      setNotification({ type: 'warning', message: t.notifications.tooFast });
      return;
    }

    // Límite de intentos
    if (submitAttempts >= 3) {
      setNotification({ type: 'error', message: t.notifications.tooMany });
      return;
    }

    setIsSubmitting(true);
    setSubmitAttempts(prev => prev + 1);
    setLastSubmit(now);

    try {
      const response = await fetch('https://a8e28svmy6.execute-api.eu-west-3.amazonaws.com/prod/contact', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'x-api-key': 'ELZglGzAcUaeexE0s2MmPhaWnRlOTN6H5YU8GY10'
        },
        body: JSON.stringify({
          ...formData,
          interests: selectedFeatures.join(', '),
          timestamp,
          timeSinceLoad,
          productName,
          source: 'product-cta'
        })
      });

      if (response.ok) {
        setNotification({ type: 'success', message: t.notifications.success });
        setFormData({ name: '', email: '', company: '', phone: '', message: '', honeypot: '' });
        setSelectedFeatures([]);
      } else {
        throw new Error('Error al enviar');
      }
    } catch (error) {
      setNotification({ type: 'error', message: t.notifications.error });
    } finally {
      setIsSubmitting(false);
    }
  };
  const steps = t.steps;

  return (
    <section className="py-24 bg-gray-50">
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="text-center mb-16">
          <h2 className="text-4xl font-light text-gray-900 mb-6">
            {getProductTitle()}
          </h2>
          <p className="text-xl text-gray-600 mb-12 max-w-3xl mx-auto">
            {getProductDescription()} {t.noCommitment}
          </p>
          {features && (
            <div className="mb-8">
              <p className="text-sm text-gray-500 mb-4 uppercase tracking-wide">{t.customizeDemo}</p>
              <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-4 max-w-4xl mx-auto">
                {features.map((feature, index) => {
                  const isSelected = selectedFeatures.includes(feature);
                  return (
                    <div
                      key={index}
                      className={`px-4 py-3 border text-sm cursor-pointer transition-all hover:shadow-md ${
                        isSelected
                          ? 'bg-blue-50 border-blue-300 text-blue-800'
                          : 'bg-white border-gray-200 text-gray-700 hover:border-gray-300'
                      }`}
                      onClick={() => toggleFeature(feature)}
                    >
                      <span
                        className={`inline-block w-2 h-2 rounded-full mr-2 ${
                          isSelected ? 'bg-blue-600' : 'bg-gray-400'
                        }`}
                      ></span>
                      {feature}
                      {isSelected && (
                        <svg className="inline-block w-4 h-4 ml-2 text-blue-600" fill="currentColor" viewBox="0 0 20 20">
                          <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                        </svg>
                      )}
                    </div>
                  );
                })}
              </div>
              {selectedFeatures.length > 0 && (
                <p className="text-sm text-blue-600 mt-4">
                  ✓ {selectedFeatures.length} {selectedFeatures.length > 1 ? t.selectedPlural : t.selectedSingular}
                </p>
              )}
            </div>
          )}
        </div>

        {/* Process Steps */}
        <div className="mb-16">
          <h3 className="text-2xl font-light text-gray-900 text-center mb-4">
            {t.processTitle}
          </h3>
          <p className="text-gray-600 text-center mb-12">{t.processSubtitle}</p>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {steps.map((step, index) => (
              <div key={index} className="text-center relative">
                {index < steps.length - 1 && (
                  <div className="hidden md:block absolute top-6 left-1/2 w-full h-0.5 bg-gray-200 z-0" style={{transform: 'translateX(50%)'}}></div>
                )}
                <div
                  className="w-12 h-12 rounded-full flex items-center justify-center text-white font-medium mb-6 mx-auto relative z-10 bg-white border-4"
                  style={{backgroundColor: '#1034ec', borderColor: '#1034ec'}}
                >
                  {step.number}
                </div>
                <h4 className="text-lg font-medium text-gray-900 mb-3">
                  {step.title}
                </h4>
                <p className="text-sm text-gray-600 leading-relaxed">
                  {step.description}
                </p>
              </div>
            ))}
          </div>
        </div>

        {/* Contact Form */}
        <div className="text-center">
          <div className="bg-white p-12 border border-gray-200 max-w-2xl mx-auto shadow-lg">
            <h3 className="text-2xl font-light text-gray-900 mb-4">
              {t.formTitle}
            </h3>
            <p className="text-gray-600 mb-2 leading-relaxed">
              {t.formSubtitle}
            </p>
            <p className="text-sm text-gray-500 mb-8">
              {t.formTagline}
            </p>

            {/* Contact Form */}
            <form className="space-y-4 text-left" onSubmit={handleSubmit}>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <input
                    type="text"
                    placeholder={t.placeholders.name}
                    value={formData.name}
                    onChange={(e) => setFormData({...formData, name: e.target.value})}
                    className="w-full px-4 py-3 border border-gray-300 text-sm focus:outline-none focus:border-blue-500"
                    required
                  />
                </div>
                <div>
                  <input
                    type="email"
                    placeholder={t.placeholders.email}
                    value={formData.email}
                    onChange={(e) => setFormData({...formData, email: e.target.value})}
                    className="w-full px-4 py-3 border border-gray-300 text-sm focus:outline-none focus:border-blue-500"
                    required
                  />
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <input
                    type="text"
                    placeholder={t.placeholders.company}
                    value={formData.company}
                    onChange={(e) => setFormData({...formData, company: e.target.value})}
                    className="w-full px-4 py-3 border border-gray-300 text-sm focus:outline-none focus:border-blue-500"
                    required
                  />
                </div>
                <div>
                  <input
                    type="tel"
                    placeholder={t.placeholders.phone}
                    value={formData.phone}
                    onChange={(e) => setFormData({...formData, phone: e.target.value})}
                    className="w-full px-4 py-3 border border-gray-300 text-sm focus:outline-none focus:border-blue-500"
                  />
                </div>
              </div>

              {selectedFeatures.length > 0 && (
                <div>
                  <label className="block text-sm text-gray-700 mb-2">{t.selectedLabel}</label>
                  <div className="bg-blue-50 p-3 border border-blue-200 rounded">
                    <div className="flex flex-wrap gap-2">
                      {selectedFeatures.map((feature, index) => (
                        <span key={index} className="inline-flex items-center px-3 py-1 bg-blue-100 text-blue-800 text-xs rounded-full">
                          {feature}
                          <button
                            type="button"
                            onClick={() => toggleFeature(feature)}
                            className="ml-2 text-blue-600 hover:text-blue-800"
                          >
                            ×
                          </button>
                        </span>
                      ))}
                    </div>
                  </div>
                </div>
              )}

              <div>
                <textarea
                  placeholder={t.placeholders.message}
                  rows="3"
                  value={formData.message}
                  onChange={(e) => setFormData({...formData, message: e.target.value})}
                  className="w-full px-4 py-3 border border-gray-300 text-sm focus:outline-none focus:border-blue-500"
                ></textarea>
              </div>

              {/* Honeypot field - hidden from users */}
              <input
                type="text"
                value={formData.honeypot}
                onChange={(e) => setFormData({...formData, honeypot: e.target.value})}
                style={{position: 'absolute', left: '-9999px', opacity: 0}}
                tabIndex="-1"
                autoComplete="off"
              />

              <div className="flex items-start">
                <input
                  type="checkbox"
                  id="privacy"
                  className="mt-1 mr-3"
                  required
                />
                <label htmlFor="privacy" className="text-xs text-gray-600">
                  {t.consent}
                </label>
              </div>

              {notification && (
                <div className={`p-4 rounded-lg ${
                  notification.type === 'success' ? 'bg-green-50 text-green-800 border border-green-200' :
                  notification.type === 'error' ? 'bg-red-50 text-red-800 border border-red-200' :
                  'bg-yellow-50 text-yellow-800 border border-yellow-200'
                }`}>
                  <div className="flex justify-between items-start">
                    <p className="text-sm">{notification.message}</p>
                    <button
                      onClick={() => setNotification(null)}
                      className="ml-4 text-gray-400 hover:text-gray-600"
                    >
                      ×
                    </button>
                  </div>
                </div>
              )}

              <button
                type="submit"
                disabled={isSubmitting}
                className={`w-full text-white px-12 py-4 text-sm font-medium uppercase tracking-wide transition-all ${isSubmitting ? 'opacity-50 cursor-not-allowed' : 'hover:shadow-lg transform hover:-translate-y-0.5'}`}
                style={{backgroundColor: '#1034ec'}}
                onMouseEnter={(e) => !isSubmitting && (e.target.style.backgroundColor = '#0d2bc7')}
                onMouseLeave={(e) => !isSubmitting && (e.target.style.backgroundColor = '#1034ec')}
              >
                {isSubmitting ? t.submit.sending : (selectedFeatures.length > 0 ? t.submit.personalized : t.submit.now)}
              </button>
            </form>


          </div>
        </div>
      </div>
    </section>
  );
};

export default ProductCTA;